// frontend/src/components/Profile/ProfileEventCard.js
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Box,
  Typography,
  Chip,
  IconButton,
  alpha
} from '@mui/material';
import {
  LocationOn as LocationOnIcon,
  People as PeopleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

// Функция для получения полного URL изображения
const getFullImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `https://fincollab.ru${path}`;
};

const ProfileEventCard = ({ event, onClick }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Получаем информацию о типе и уровне из event
  const getTypeInfo = () => {
    const typeMap = {
      'conference': { label: 'Конференция', icon: '🗣️', color: '#4361ee' },
      'seminar': { label: 'Семинар', icon: '📚', color: '#3a0ca3' },
      'symposium': { label: 'Симпозиум', icon: '🔬', color: '#7209b7' },
      'workshop': { label: 'Воркшоп', icon: '🛠️', color: '#f72585' },
      'school': { label: 'Школа', icon: '🏫', color: '#4cc9f0' },
      'congress': { label: 'Конгресс', icon: '🌍', color: '#4895ef' },
      'forum': { label: 'Форум', icon: '🗣️', color: '#560bad' },
      'roundtable': { label: 'Круглый стол', icon: '🔄', color: '#b5179e' },
      'competition': { label: 'Конкурс', icon: '🏆', color: '#f8961e' },
      'festival': { label: 'Фестиваль', icon: '🎪', color: '#f94144' }
    };
    return typeMap[event.type] || { label: event.type, icon: '📅', color: '#757575' };
  };

  const getLevelInfo = () => {
    const levelMap = {
      'international': { label: 'Международный', color: '#9C27B0' },
      'national': { label: 'Всероссийский', color: '#2196F3' },
      'interregional': { label: 'Межрегиональный', color: '#4CAF50' },
      'regional': { label: 'Региональный', color: '#FF9800' },
      'university': { label: 'Внутривузовский', color: '#795548' },
      'interuniversity': { label: 'Межвузовский', color: '#FF6B6B' }
    };
    return levelMap[event.level] || { label: event.level, color: '#757575' };
  };

  const typeInfo = getTypeInfo();
  const levelInfo = getLevelInfo();

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, 'd MMMM yyyy', { locale: ru });
    } catch {
      return dateString;
    }
  };

  const handlePrevImage = (e) => {
    e.stopPropagation();
    if (!event.cover_images?.length) return;
    setCurrentImageIndex((prev) =>
      prev === 0 ? event.cover_images.length - 1 : prev - 1
    );
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    if (!event.cover_images?.length) return;
    setCurrentImageIndex((prev) =>
      prev === event.cover_images.length - 1 ? 0 : prev + 1
    );
  };

  const handleImageClick = (e) => {
    e.stopPropagation();
    if (event.cover_images?.length > 1) {
      handleNextImage(e);
    }
  };

  return (
    <Card
      onClick={onClick}
      sx={{
        width: '100%',
        maxWidth: 290,  // 🔥 ОГРАНИЧИВАЕМ МАКСИМАЛЬНУЮ ШИРИНУ (как в EventsPage)
        height: '100%',
        cursor: 'pointer',
        borderRadius: 7,
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 0,  // 🔥 УМЕНЬШЕНА ТЕНЬ (было 2)
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 1  // 🔥 УМЕНЬШЕНА ТЕНЬ ПРИ НАВЕДЕНИИ (было 4)
        }
      }}
    >
      <Box sx={{ position: 'relative', height: 160, overflow: 'hidden', flexShrink: 0 }}>
        {event.cover_images && event.cover_images.length > 0 ? (
          <>
            <CardMedia
              component="img"
              height="160"
              image={getFullImageUrl(event.cover_images[currentImageIndex])}
              alt={event.title}
              sx={{
                objectFit: 'cover',
                cursor: 'pointer'
              }}
              onClick={handleImageClick}
            />

            {/* Тёмный оверлей */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.2) 100%)',
                zIndex: 1
              }}
            />

            {/* Название по центру */}
            <Typography
              variant="h6"
              fontWeight="800"
              textTransform="uppercase"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '90%',
                color: 'white',
                textAlign: 'center',
                zIndex: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.3,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              {event.title}
            </Typography>

            {/* ТИП МЕРОПРИЯТИЯ - в левом нижнем углу */}
            <Chip
              label={`${typeInfo.icon} ${typeInfo.label}`}
              size="small"
              sx={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                bgcolor: alpha(typeInfo.color, 0.85),
                color: 'white',
                fontWeight: 500,
                borderRadius: 16,
                height: 24,
                zIndex: 3,
                fontSize: '0.7rem',
                '& .MuiChip-label': { px: 1 }
              }}
            />

            {/* ДАТА - в правом нижнем углу */}
            <Chip
              label={formatDate(event.start_date)}
              size="small"
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                bgcolor: alpha('#000000', 0.6),
                color: 'white',
                fontWeight: 500,
                borderRadius: 16,
                height: 24,
                zIndex: 3,
                fontSize: '0.7rem',
                backdropFilter: 'blur(4px)',
                '& .MuiChip-label': { px: 1 }
              }}
            />

            {/* Стрелки навигации */}
            {event.cover_images.length > 1 && (
              <>
                <IconButton
                  size="small"
                  onClick={handlePrevImage}
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(0,0,0,0.7)'
                    },
                    zIndex: 3
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleNextImage}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(0,0,0,0.7)'
                    },
                    zIndex: 3
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>

                {/* Индикатор */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    display: 'flex',
                    gap: 0.5,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    borderRadius: 2,
                    px: 1,
                    py: 0.5,
                    zIndex: 3
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'white' }}>
                    {currentImageIndex + 1}/{event.cover_images.length}
                  </Typography>
                </Box>
              </>
            )}
          </>
        ) : (
          // Если нет фото
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(typeInfo.color, 0.1)
            }}
          >
            <Typography
              variant="h6"
              fontWeight="800"
              textTransform="uppercase"
              sx={{
                width: '90%',
                textAlign: 'center',
                color: typeInfo.color,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.3
              }}
            >
              {event.title}
            </Typography>

            {/* Тип в левом нижнем углу (без фото) */}
            <Chip
              label={`${typeInfo.icon} ${typeInfo.label}`}
              size="small"
              sx={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                bgcolor: alpha(typeInfo.color, 0.85),
                color: 'white',
                fontWeight: 500,
                borderRadius: 16,
                height: 24,
                zIndex: 3,
                fontSize: '0.7rem',
                '& .MuiChip-label': { px: 1 }
              }}
            />

            {/* Дата в правом нижнем углу (без фото) */}
            <Chip
              label={formatDate(event.start_date)}
              size="small"
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                bgcolor: alpha('#000000', 0.6),
                color: 'white',
                fontWeight: 500,
                borderRadius: 16,
                height: 24,
                zIndex: 3,
                fontSize: '0.7rem',
                backdropFilter: 'blur(4px)',
                '& .MuiChip-label': { px: 1 }
              }}
            />
          </Box>
        )}

        {/* Уровень мероприятия - в правом верхнем углу */}
        <Chip
          label={levelInfo.label}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: levelInfo.color,
            color: 'white',
            fontWeight: 600,
            fontSize: '0.65rem',
            borderRadius: 16,
            height: 20,
            zIndex: 3,
            '& .MuiChip-label': { px: 1 }
          }}
        />
      </Box>

      {/* Контент ниже фото */}
      <CardContent sx={{
        p: 2,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        '&:last-child': { pb: 2 }
      }}>
        {/* Описание */}
        <Typography
          variant="body2"
          sx={{
            mb: 1.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.4,
            fontWeight: 300,
            fontSize: '0.8rem',
            color: 'text.secondary',
            textAlign: 'left'
          }}
        >
          {event.short_description || event.description || 'Описание отсутствует'}
        </Typography>

        {/* Филиал */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 1,
        }}>
          <LocationOnIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.7rem' }}>
            {event.organizer_branches?.join(', ') || 'Филиал не указан'}
          </Typography>
        </Box>

        {/* Участники */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mt: 'auto'
        }}>
          <PeopleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            {event.participant_count || 0} участников
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProfileEventCard;